import { useState, useEffect } from 'react';
import { useMutation, useAction } from 'convex/react';
import { Trash2, Plus, Upload } from 'lucide-react';
import { api } from '../../convex/_generated/api';
import type { ContentInput } from '../lib/types';
import { useScanner } from '../hooks/useScanner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

interface ScanFormProps {
  onScanStarted: (batchId: string) => void;
  initialContents?: ContentInput[];
}

export default function ScanForm({ onScanStarted, initialContents }: ScanFormProps) {
  const [contents, setContents] = useState<ContentInput[]>(
    initialContents || [{ title: '', year: '', type: 'series', priority: 'medium' }]
  );
  const [isScanning, setIsScanning] = useState(false);

  const createScan = useMutation(api.scans.createScan);
  const triggerManualScan = useAction(api.triggerScans.triggerManualScan);
  const { runScan } = useScanner();

  // Check if server-side scans are enabled
  const useServerSideScans = import.meta.env.VITE_SERVER_SIDE_SCANS === 'true';

  // Load from localStorage on mount
  useEffect(() => {
    if (!initialContents) {
      const saved = localStorage.getItem('scanFormState');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setContents(parsed);
          }
        } catch (e) {
          console.error('Failed to parse saved form state');
        }
      }
    }
  }, [initialContents]);

  // Save to localStorage when contents change
  useEffect(() => {
    if (!initialContents && contents.some(c => c.title.trim())) {
      localStorage.setItem('scanFormState', JSON.stringify(contents));
    }
  }, [contents, initialContents]);

  // Update contents when initialContents prop changes
  useEffect(() => {
    if (initialContents) {
      setContents(initialContents);
    }
  }, [initialContents]);

  const addContentRow = () => {
    setContents([
      ...contents,
      { title: '', year: '', type: 'series', priority: 'medium' },
    ]);
  };

  const clearAll = () => {
    if (!contents.length || contents.every(c => !c.title && !c.year)) return;
    const confirmClear = confirm('Clear all titles from the list?');
    if (!confirmClear) return;
    try { localStorage.removeItem('scanFormState'); } catch {}
    setContents([{ title: '', year: '', type: 'series', priority: 'medium' }]);
  };

  const removeContentRow = (index: number) => {
    setContents(contents.filter((_, i) => i !== index));
  };

  const updateContent = (index: number, field: keyof ContentInput, value: string) => {
    const updated = [...contents];
    updated[index] = { ...updated[index], [field]: value };
    setContents(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validContents = contents.filter(c => c.title.trim());
    if (validContents.length === 0) {
      alert('Please enter at least one content title');
      return;
    }

    setIsScanning(true);
    const batchId = `batch_${Date.now()}`;

    // Navigate immediately so the progress UI shows up, even for large CSVs
    onScanStarted(batchId);

    try {
      if (useServerSideScans) {
        // SERVER-SIDE EXECUTION: Create scan rows up-front, then trigger Trigger.dev tasks
        console.log('Using server-side scans via Trigger.dev');

        // Step 1: Create all scan records immediately (instant visibility in History)
        const scanRecords = await Promise.allSettled(
          validContents.map(content =>
            createScan({
              batchId,
              contentTitle: content.title,
              arabicTitle: content.arabicTitle,
              contentYear: content.year,
              contentType: content.type,
              priority: content.priority,
            })
          )
        );

        // Step 2: Trigger Trigger.dev tasks with scanIds
        const triggerResults = await Promise.allSettled(
          scanRecords.map((result, index) => {
            if (result.status === 'fulfilled') {
              const scanId = result.value;
              const content = validContents[index];
              return triggerManualScan({
                scanId: scanId as any, // Pass existing scanId
                batchId,
                contentTitle: content.title,
                arabicTitle: content.arabicTitle,
                contentYear: content.year,
                contentType: content.type,
                priority: content.priority,
              });
            } else {
              return Promise.reject(result.reason);
            }
          })
        );

        const createFailed = scanRecords.filter(r => r.status === 'rejected').length;
        const triggerFailed = triggerResults.filter(r => r.status === 'rejected').length;

        if (createFailed > 0 || triggerFailed > 0) {
          console.warn(`${createFailed} scans failed to create, ${triggerFailed} failed to queue`);
        }

        console.log(`${validContents.length - createFailed - triggerFailed} scans queued server-side for batch: ${batchId}`);

      } else {
        // CLIENT-SIDE EXECUTION (legacy): Run in browser
        console.log('Using client-side scans (browser execution)');

        // Create a task per content that creates the scan in Convex then runs it
        const tasks = validContents.map((content) => async () => {
          const scanId = await createScan({
            batchId,
            contentTitle: content.title,
            arabicTitle: content.arabicTitle,
            contentYear: content.year,
            contentType: content.type,
            priority: content.priority,
          });
          await runScan(scanId, content).catch(err => {
            console.error(`Failed to scan ${content.title}:`, err);
          });
        });

        // Run with limited concurrency (max 4 titles at a time)
        const limit = 4;
        let idx = 0;
        const inFlight: Promise<void>[] = [];

        // Track when ALL tasks (not just initial batch) complete
        let allTasksStarted = false;
        const checkComplete = () => {
          return allTasksStarted && inFlight.length === 0;
        };

        const launchNext = () => {
          if (idx >= tasks.length) {
            allTasksStarted = true;
            return;
          }
          const p = tasks[idx++]().finally(() => {
            const i = inFlight.indexOf(p);
            if (i >= 0) inFlight.splice(i, 1);
            launchNext();
          });
          inFlight.push(p);
        };

        // Prime the pool
        for (let i = 0; i < Math.min(limit, tasks.length); i++) launchNext();

        // Wait for ALL tasks to complete (not just initial batch)
        while (!checkComplete()) {
          await Promise.allSettled([...inFlight]);
        }

        console.log('Scans scheduled for batch:', batchId);
      }

      // Clear persisted form so the next visit starts clean
      try { localStorage.removeItem('scanFormState'); } catch {}
      // Reset to a single empty row for convenience
      setContents([{ title: '', year: '', type: 'series', priority: 'medium' }]);

    } catch (error) {
      console.error('Failed to start scan:', error);
      alert('Failed to start scan. Please try again.');
    } finally {
      setIsScanning(false);
    }
  };

  const handleCSVUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const csv = event.target?.result as string;
      const lines = csv.split('\n');
      const parsed: ContentInput[] = [];

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        const [title, arabicTitle, year, type, priority] = line.split(',').map(s => s.trim());
        if (title) {
          parsed.push({
            title,
            arabicTitle: arabicTitle || undefined,
            year: year || undefined,
            type: (type as any) || 'series',
            priority: (priority as any) || 'medium',
          });
        }
      }

      if (parsed.length > 0) {
        setContents(parsed);
      }
    };

    reader.readAsText(file);
  };

  return (
    <Card>
      <CardContent>
        {!useServerSideScans && (
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start gap-3">
              <span className="text-yellow-600 text-xl">⚠️</span>
              <div className="flex-1">
                <h4 className="font-semibold text-yellow-900 mb-1">Important: Keep Browser Open</h4>
                <p className="text-sm text-yellow-800">
                  Scans run in your browser and will fail if you close this window.
                  Large batches may take several minutes. For automated scanning, use the Monitoring feature instead.
                </p>
              </div>
            </div>
          </div>
        )}
        {useServerSideScans && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-3">
              <span className="text-blue-600 text-xl">✓</span>
              <div className="flex-1">
                <h4 className="font-semibold text-blue-900 mb-1">Server-Side Scanning Enabled</h4>
                <p className="text-sm text-blue-800">
                  Scans run on the server and will continue even if you close your browser.
                  You can safely navigate away after submitting. View progress in the History page.
                </p>
              </div>
            </div>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label htmlFor="csv-upload" className="text-sm font-medium flex items-center gap-2">
              <Upload className="h-4 w-4" />
              Or upload CSV file (title, arabicTitle, year, type, priority):
            </label>
            <Input
              id="csv-upload"
              type="file"
              accept=".csv"
              onChange={handleCSVUpload}
              className="cursor-pointer"
            />
          </div>

          <div className="space-y-4">
            {contents.map((content, index) => (
              <div key={index} className="flex gap-3 items-start">
                <div className="flex-1 space-y-3 md:space-y-0 md:grid md:grid-cols-12 md:gap-3">
                  <div className="md:col-span-3">
                    <Input
                      type="text"
                      placeholder="Content Title *"
                      value={content.title}
                      onChange={(e) => updateContent(index, 'title', e.target.value)}
                      required
                    />
                  </div>

                  <div className="md:col-span-3">
                    <Input
                      type="text"
                      placeholder="Arabic Content Title (Optional)"
                      value={content.arabicTitle || ''}
                      onChange={(e) => updateContent(index, 'arabicTitle', e.target.value)}
                      dir="rtl"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Input
                      type="text"
                      placeholder="Year"
                      value={content.year || ''}
                      onChange={(e) => updateContent(index, 'year', e.target.value)}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Select
                      value={content.type}
                      onValueChange={(value) => updateContent(index, 'type', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Series" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="series">Series</SelectItem>
                        <SelectItem value="film">Film</SelectItem>
                        <SelectItem value="original">OSN Original</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="md:col-span-2">
                    <Select
                      value={content.priority}
                      onValueChange={(value) => updateContent(index, 'priority', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Medium Priority" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="high">High Priority</SelectItem>
                        <SelectItem value="medium">Medium Priority</SelectItem>
                        <SelectItem value="low">Low Priority</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {contents.length > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => removeContentRow(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>

          <div className="flex gap-3 justify-end">
            <Button type="button" variant="outline" onClick={addContentRow}>
              <Plus className="mr-2 h-4 w-4" />
              Add Another Title
            </Button>

            <Button type="button" variant="secondary" onClick={clearAll} disabled={contents.length === 1 && !contents[0].title && !contents[0].year}>
              <Trash2 className="mr-2 h-4 w-4" />
              Clear All
            </Button>

            <Button type="submit" disabled={isScanning}>
              {isScanning ? 'Starting Scan...' : 'Start Scan'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
